import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { api, API_BASE } from '../services/api';
import { useCall } from './CallProvider';


const phaseText: Record<string, string> = {
  preparing: 'Подготовка звонка…',
  ringing: 'Вызов…',
  incoming: 'Входящий звонок',
  connecting: 'Соединение…',
  active: 'Идёт звонок',
  ended: 'Звонок завершён',
};

const reasonText: Record<string, string> = {
  busy: 'Занят',
  unavailable: 'Вызов недоступен',
  forbidden: 'Вызов недоступен',
  declined: 'Вызов отклонён',
  missed: 'Нет ответа',
  timeout: 'Нет ответа',
  media_denied: 'Нет доступа к камере или микрофону',
  signaling_unavailable: 'Сервис звонков недоступен',
  ice_failed: 'Не удалось установить соединение',
  hangup: 'Звонок завершён',
};

const streamSource = (url: string | null): string | null => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${API_BASE}${url}`;
};

const formatDuration = (seconds: number) => {
  const minutes = Math.floor(seconds / 60).toString().padStart(2, '0');
  const remainder = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${minutes}:${remainder}`;
};

export default function CallOverlay() {
  const {
    state, localStream, remoteStream, acceptCall, declineCall, hangup,
    toggleMute, toggleCamera, reset,
  } = useCall();
  const [peer, setPeer] = useState<{ name: string; avatarUrl: string | null } | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const firstActionRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasVisibleRef = useRef(false);
  const visible = state.phase !== 'idle';

  useEffect(() => {
    if (!visible || state.peerId === null) {
      setPeer(null);
      return;
    }
    if (state.peerName) {
      setPeer({ name: state.peerName, avatarUrl: state.peerAvatarUrl });
      return;
    }
    let active = true;
    void api.get(`/auth/user/${state.peerId}`).then(({ data }) => {
      if (active) setPeer({ name: data.username ?? `Пользователь #${state.peerId}`, avatarUrl: data.avatar_url ?? null });
    }).catch(() => {
      if (active) setPeer({ name: `Пользователь #${state.peerId}`, avatarUrl: null });
    });
    return () => { active = false; };
  }, [state.peerAvatarUrl, state.peerId, state.peerName, visible]);

  useEffect(() => {
    if (visible && !wasVisibleRef.current) {
      returnFocusRef.current = document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
      requestAnimationFrame(() => firstActionRef.current?.focus());
    }
    if (!visible && wasVisibleRef.current) returnFocusRef.current?.focus();
    wasVisibleRef.current = visible;
  }, [visible]);

  useEffect(() => {
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = remoteStream;
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = remoteStream;
    if (localVideoRef.current) localVideoRef.current.srcObject = localStream;
    return () => {
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
      if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
      if (localVideoRef.current) localVideoRef.current.srcObject = null;
    };
  }, [localStream, remoteStream, state.callType]);

  useEffect(() => {
    if (state.phase !== 'active' || state.connectedAt === null) {
      setElapsed(0);
      return;
    }
    const update = () => setElapsed(Math.max(0, Math.floor((Date.now() - state.connectedAt!) / 1000)));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [state.connectedAt, state.phase]);

  if (!visible) return null;

  const isVideo = state.callType === 'video';
  const incoming = state.phase === 'incoming';
  const canCancel = state.phase === 'preparing' || state.phase === 'ringing';
  const activeOrConnecting = state.phase === 'active' || state.phase === 'connecting';
  const title = peer?.name ?? (state.peerId ? `Пользователь #${state.peerId}` : 'Звонок');
  const avatarUrl = streamSource(peer?.avatarUrl ?? null);
  const terminalText = state.error
    ? reasonText[state.error] ?? 'Ошибка звонка'
    : state.endedReason
      ? reasonText[state.endedReason] ?? phaseText.ended
      : phaseText[state.phase];

  return createPortal(
    <div
      className="fixed inset-0 z-[11000] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`${isVideo ? 'Видеозвонок' : 'Аудиозвонок'} с ${title}`}
    >
      <section className="relative flex max-h-[calc(100vh-2rem)] w-full max-w-3xl flex-col overflow-hidden rounded-3xl border border-gray-700 bg-[#151820] shadow-2xl">
        <div className={`relative flex min-h-[22rem] items-center justify-center overflow-hidden ${isVideo ? 'bg-black' : 'bg-gradient-to-br from-[#20283a] to-[#11141b]'}`}>
          {isVideo && remoteStream ? (
            <video ref={remoteVideoRef} autoPlay playsInline className="h-full max-h-[70vh] w-full object-contain" />
          ) : (
            <div className="flex flex-col items-center px-6 text-center">
              {avatarUrl ? (
                <img src={avatarUrl} alt={title} className="h-28 w-28 rounded-full border-4 border-white/10 object-cover shadow-xl" />
              ) : (
                <div className="flex h-28 w-28 items-center justify-center rounded-full bg-blue-600 text-4xl font-bold text-white shadow-xl">
                  {title.slice(0, 1).toUpperCase()}
                </div>
              )}
              <h2 className="mt-5 text-2xl font-bold text-white">{title}</h2>
            </div>
          )}

          {!isVideo && <audio ref={remoteAudioRef} autoPlay />}
          {isVideo && localStream && !state.cameraOff && (
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              aria-label="Ваше видео"
              className="absolute bottom-4 right-4 h-28 w-20 rounded-2xl border border-white/20 bg-gray-950 object-cover shadow-xl sm:h-36 sm:w-28"
            />
          )}

          <div className="absolute left-0 right-0 top-0 bg-gradient-to-b from-black/70 to-transparent p-5 text-center">
            <p className="font-semibold text-white">{isVideo ? 'Видеозвонок' : 'Аудиозвонок'}</p>
            <p className="mt-1 text-sm text-gray-300">
              {state.phase === 'active' ? formatDuration(elapsed) : terminalText}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 border-t border-gray-800 bg-[#1a1d24] p-5">
          {incoming && (
            <>
              <button ref={firstActionRef} type="button" onClick={() => void acceptCall()} aria-label="Принять звонок" className="rounded-full bg-emerald-600 px-6 py-3 font-bold text-white transition hover:bg-emerald-500">Принять</button>
              <button type="button" onClick={declineCall} aria-label="Отклонить звонок" className="rounded-full bg-red-600 px-6 py-3 font-bold text-white transition hover:bg-red-500">Отклонить</button>
            </>
          )}
          {canCancel && (
            <button ref={firstActionRef} type="button" onClick={() => hangup('cancelled')} aria-label="Отменить звонок" className="rounded-full bg-red-600 px-6 py-3 font-bold text-white transition hover:bg-red-500">Отменить</button>
          )}
          {activeOrConnecting && (
            <>
              <button ref={firstActionRef} type="button" onClick={toggleMute} aria-label={state.muted ? 'Включить микрофон' : 'Выключить микрофон'} aria-pressed={state.muted} className={`rounded-full px-5 py-3 font-bold transition ${state.muted ? 'bg-blue-600 text-white' : 'bg-gray-700 text-white hover:bg-gray-600'}`}>{state.muted ? '🔇' : '🎙️'}</button>
              {isVideo && (
                <button type="button" onClick={toggleCamera} aria-label={state.cameraOff ? 'Включить камеру' : 'Выключить камеру'} aria-pressed={state.cameraOff} className={`rounded-full px-5 py-3 font-bold transition ${state.cameraOff ? 'bg-blue-600 text-white' : 'bg-gray-700 text-white hover:bg-gray-600'}`}>{state.cameraOff ? '📷' : '🎥'}</button>
              )}
              <button type="button" onClick={() => hangup()} aria-label="Завершить звонок" className="rounded-full bg-red-600 px-6 py-3 font-bold text-white transition hover:bg-red-500">Завершить</button>
            </>
          )}
          {state.phase === 'ended' && (
            <button ref={firstActionRef} type="button" onClick={reset} aria-label="Закрыть окно звонка" className="rounded-xl bg-blue-600 px-6 py-3 font-bold text-white transition hover:bg-blue-500">Закрыть</button>
          )}
        </div>
      </section>
    </div>,
    document.body,
  );
}
